/**
 * @file Imports SVG icon assets and maps them to MIME types.
 * Uses a predefined mapping from MIME types to icon identifiers (`mapMimeToIcon`) and resolves these identifiers to the actual imported SVG assets.
 * Exports the final object mapping MIME types directly to SVG assets.
 */
import mapMimeToIcon from './map-mime-to-icon';
// Import SVG assets
import aiIcon from './img/ai.svg';
import aviIcon from './img/avi.svg';
import bmpIcon from './img/bmp.svg';
import csvIcon from './img/csv.svg';
import defaultIcon from './img/default.svg';
import dllIcon from './img/dll.svg';
import docIcon from './img/doc.svg';
import docxIcon from './img/docx.svg';
import dwgIcon from './img/dwg.svg';
import exeIcon from './img/exe.svg';
import flvIcon from './img/flv.svg';
import gifIcon from './img/gif.svg';
import htmlIcon from './img/html.svg';
import isoIcon from './img/iso.svg';
import javaIcon from './img/java.svg';
import jsonIcon from './img/json.svg';
import jpgIcon from './img/jpg.svg';
import mdbIcon from './img/mdb.svg';
import midIcon from './img/mid.svg';
import movIcon from './img/mov.svg';
import mp3Icon from './img/mp3.svg';
import mp4Icon from './img/mp4.svg';
import mpegIcon from './img/mpeg.svg';
import pdfIcon from './img/pdf.svg';
import pngIcon from './img/png.svg';
import pptIcon from './img/ppt.svg';
import psdIcon from './img/psd.svg';
import pubIcon from './img/pub.svg';
import rarIcon from './img/rar.svg';
import rssIcon from './img/rss.svg';
import svgIcon from './img/svg.svg';
import tiffIcon from './img/tiff.svg';
import txtIcon from './img/txt.svg';
import wavIcon from './img/wav.svg';
import wmaIcon from './img/wma.svg';
import xlsIcon from './img/xls.svg';
import xmlIcon from './img/xml.svg';
import zipIcon from './img/zip.svg';
/**
 * An intermediate object mapping icon identifier strings (e.g., 'pdfIcon') to the actual imported SVG icon assets.
 * @type {Record<string, string>}
 */
const icons = {
    aiIcon: aiIcon,
    aviIcon: aviIcon,
    bmpIcon: bmpIcon,
    csvIcon: csvIcon,
    defaultIcon: defaultIcon,
    dllIcon: dllIcon,
    docIcon: docIcon,
    docxIcon: docxIcon,
    dwgIcon: dwgIcon,
    exeIcon: exeIcon,
    flvIcon: flvIcon,
    gifIcon: gifIcon,
    htmlIcon: htmlIcon,
    isoIcon: isoIcon,
    javaIcon: javaIcon,
    jsonIcon: jsonIcon,
    jpgIcon: jpgIcon,
    mdbIcon: mdbIcon,
    midIcon: midIcon,
    movIcon: movIcon,
    mp3Icon: mp3Icon,
    mp4Icon: mp4Icon,
    mpegIcon: mpegIcon,
    pdfIcon: pdfIcon,
    pngIcon: pngIcon,
    pptIcon: pptIcon,
    psdIcon: psdIcon,
    pubIcon: pubIcon,
    rarIcon: rarIcon,
    rssIcon: rssIcon,
    svgIcon: svgIcon,
    tiffIcon: tiffIcon,
    txtIcon: txtIcon,
    wavIcon: wavIcon,
    wmaIcon: wmaIcon,
    xlsIcon: xlsIcon,
    xmlIcon: xmlIcon,
    zipIcon: zipIcon
};
/**
 * The final mapping object where keys are MIME type strings and values are the corresponding imported SVG icon assets.
 * Constructed by iterating through `mapMimeToIcon` and resolving the icon identifier string using the `icons` object.
 * Includes a 'default' key mapping to the default icon asset.
 *
 * @type {Record<string, string>}
 */
const fileTypeIcon = {};
// Use Object.entries to get both key (mime) and value (iconID) from the map
Object.entries(mapMimeToIcon).forEach(([mime, iconID]) => {
    // Check if the iconID exists in the icons map before assigning
    if (icons[iconID]) {
        fileTypeIcon[mime] = icons[iconID];
    }
    else {
        // Assign default icon if the specific one isn't found (including for 'default' itself if needed)
        if (!fileTypeIcon[mime] && icons['defaultIcon']) {
            fileTypeIcon[mime] = icons['defaultIcon'];
        }
    }
});
// Ensure the default icon is set if it somehow wasn't processed (e.g., if 'default' wasn't in mapMimeToIcon but should be handled)
if (!fileTypeIcon['default'] && icons['defaultIcon']) {
    fileTypeIcon['default'] = icons['defaultIcon'];
}
export default fileTypeIcon;
